import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

// Types for Supabase query results with joins
interface UnitInterestWithUnit {
  unit_id: string;
  view_count: number;
  form_submitted: boolean;
  engagement_score: number;
  property_units: {
    unit_type: Database["public"]["Enums"]["unit_type_code"];
  };
}

interface UnitAggregation {
  unit_number: string;
  unit_type: string;
  inquiry_count: number;
  total_engagement: number;
}

interface ConversionAggregation {
  unit_type: string;
  total_views: number;
  total_conversions: number;
}

export interface LeadCaptureMetrics {
  totalLeads: number;
  leadsBySource: Array<{ source: string; count: number }>;
  leadsByInterestType: Array<{ type: string; count: number }>;
  averageEngagementScore: number;
  topPerformingUnits: Array<{
    unit_number: string;
    unit_type: string;
    inquiry_count: number;
    avg_engagement: number;
  }>;
}

export interface UnitInterestAnalytics {
  mostViewedUnits: Array<{
    unit_number: string;
    unit_type: string;
    view_count: number;
  }>;
  mostClickedUnits: Array<{
    unit_number: string;
    unit_type: string;
    click_count: number;
  }>;
  hiddenPriceInquiries: Array<{
    unit_number: string;
    unit_type: string;
    inquiry_count: number;
  }>;
  conversionByType: Array<{
    unit_type: string;
    total_views: number;
    total_conversions: number;
    conversion_rate: number;
  }>;
}

export interface DealSyncPerformance {
  totalDealsCreated: number;
  successRate: number;
  failedSyncs: number;
  recentActivity: Array<{
    timestamp: string;
    status: string;
    unit_number?: string;
    contact_email?: string;
  }>;
}

export interface HubSpotContactsAnalytics {
  totalContacts: number;
  contactsByLifecycleStage: Array<{ stage: string; count: number }>;
  recentContacts: number; // Last 7 days
  contactsGrowth: number; // Percentage change
}

export interface HubSpotDealsAnalytics {
  totalDeals: number;
  dealsByStage: Array<{ stage: string; count: number; value: number }>;
  pipelineValue: number;
  averageDealSize: number;
  conversionRate: number; // Leads to deals
  wonDeals: number;
  lostDeals: number;
}

export interface HubSpotEngagementMetrics {
  formSubmissions: number;
  meetingsBooked: number;
  emailOpens: number;
  emailClicks: number;
}

/**
 * Get lead capture metrics from unit_interest_tracking table
 */
export async function getLeadCaptureMetrics(): Promise<LeadCaptureMetrics | null> {
  try {
    // Get total leads
    const { count: totalLeads } = await supabase
      .from('unit_interest_tracking')
      .select('*', { count: 'exact', head: true });

    // Get leads by source
    const { data: leadSourceData } = await supabase
      .from('unit_interest_tracking')
      .select('lead_source');

    const leadsBySource = leadSourceData?.reduce((acc: Record<string, number>, item) => {
      const leadSource = item.lead_source as { source?: string } | null;
      const source = leadSource?.source || 'Unknown';
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    }, {});

    const leadsBySourceArray = Object.entries(leadsBySource || {}).map(([source, count]) => ({
      source,
      count: count as number,
    }));

    // Get leads by interest type (form_submitted vs just viewed)
    const { count: formSubmittedCount } = await supabase
      .from('unit_interest_tracking')
      .select('*', { count: 'exact', head: true })
      .eq('form_submitted', true);

    const { count: meetingScheduledCount } = await supabase
      .from('unit_interest_tracking')
      .select('*', { count: 'exact', head: true })
      .eq('meeting_scheduled', true);

    const leadsByInterestType = [
      { type: 'Form Submitted', count: formSubmittedCount || 0 },
      { type: 'Meeting Scheduled', count: meetingScheduledCount || 0 },
      { type: 'Viewed Only', count: (totalLeads || 0) - (formSubmittedCount || 0) },
    ];

    // Get average engagement score
    const { data: engagementData } = await supabase
      .from('unit_interest_tracking')
      .select('engagement_score');

    const averageEngagementScore =
      engagementData && engagementData.length > 0
        ? engagementData.reduce((sum, item) => sum + (item.engagement_score || 0), 0) /
          engagementData.length
        : 0;

    // Get top performing units
    const { data: topUnitsData } = await supabase
      .from('unit_interest_tracking')
      .select(
        `
        unit_id,
        engagement_score,
        property_units!inner(unit_number, unit_type)
      `
      )
      .order('engagement_score', { ascending: false })
      .limit(10);

    // Aggregate by unit
    const unitAggregation = topUnitsData?.reduce((acc: Record<string, UnitAggregation>, item: { engagement_score?: number; property_units?: { unit_number?: string; unit_type?: string } }) => {
      const unitNumber = item.property_units?.unit_number;
      const unitType = item.property_units?.unit_type;
      if (!unitNumber) return acc;

      if (!acc[unitNumber]) {
        acc[unitNumber] = {
          unit_number: unitNumber,
          unit_type: unitType,
          inquiry_count: 0,
          total_engagement: 0,
        };
      }
      acc[unitNumber].inquiry_count += 1;
      acc[unitNumber].total_engagement += item.engagement_score || 0;
      return acc;
    }, {});

    const topPerformingUnits = Object.values(unitAggregation || {})
      .map((unit: UnitAggregation) => ({
        unit_number: unit.unit_number,
        unit_type: unit.unit_type,
        inquiry_count: unit.inquiry_count,
        avg_engagement: unit.total_engagement / unit.inquiry_count,
      }))
      .sort((a, b) => b.inquiry_count - a.inquiry_count)
      .slice(0, 5);

    return {
      totalLeads: totalLeads || 0,
      leadsBySource: leadsBySourceArray,
      leadsByInterestType,
      averageEngagementScore: Math.round(averageEngagementScore * 10) / 10,
      topPerformingUnits,
    };
  } catch (error) {
    console.error('Error fetching lead capture metrics:', error);
    return null;
  }
}

/**
 * Get unit interest analytics
 */
export async function getUnitInterestAnalytics(): Promise<UnitInterestAnalytics | null> {
  try {
    // Most viewed units
    const { data: viewedUnitsData } = await supabase
      .from('unit_interest_tracking')
      .select(
        `
        unit_id,
        view_count,
        property_units!inner(unit_number, unit_type)
      `
      )
      .order('view_count', { ascending: false })
      .limit(5);

    const mostViewedUnits = viewedUnitsData?.map((item: { view_count?: number; property_units?: { unit_number?: string; unit_type?: string } }) => ({
      unit_number: item.property_units?.unit_number,
      unit_type: item.property_units?.unit_type,
      view_count: item.view_count || 0,
    })) || [];

    // Most clicked units (on site plan)
    const { data: clickedUnitsData } = await supabase
      .from('unit_interest_tracking')
      .select(
        `
        unit_id,
        click_count,
        property_units!inner(unit_number, unit_type)
      `
      )
      .order('click_count', { ascending: false })
      .limit(5);

    const mostClickedUnits = clickedUnitsData?.map((item: { click_count?: number; property_units?: { unit_number?: string; unit_type?: string } }) => ({
      unit_number: item.property_units?.unit_number,
      unit_type: item.property_units?.unit_type,
      click_count: item.click_count || 0,
    })) || [];

    // Units with hidden prices generating most inquiries
    const { data: hiddenPriceData } = await supabase
      .from('unit_interest_tracking')
      .select(
        `
        unit_id,
        form_submitted,
        property_units!inner(unit_number, unit_type, show_price)
      `
      )
      .eq('property_units.show_price', false)
      .eq('form_submitted', true);

    const hiddenPriceAggregation = hiddenPriceData?.reduce((acc: Record<string, UnitAggregation>, item: { property_units?: { unit_number?: string; unit_type?: string } }) => {
      const unitNumber = item.property_units?.unit_number;
      if (!unitNumber) return acc;

      if (!acc[unitNumber]) {
        acc[unitNumber] = {
          unit_number: unitNumber,
          unit_type: item.property_units?.unit_type,
          inquiry_count: 0,
          total_engagement: 0,
        };
      }
      acc[unitNumber].inquiry_count += 1;
      return acc;
    }, {});

    const hiddenPriceInquiries = Object.values(hiddenPriceAggregation || {})
      .sort((a, b) => b.inquiry_count - a.inquiry_count)
      .slice(0, 5);

    // Conversion rate by unit type
    const { data: conversionData } = await supabase
      .from('unit_interest_tracking')
      .select(
        `
        view_count,
        form_submitted,
        property_units!inner(unit_type)
      `
      );

    const conversionAggregation = conversionData?.reduce((acc: Record<string, ConversionAggregation>, item: UnitInterestWithUnit) => {
      const unitType = item.property_units?.unit_type;
      if (!unitType) return acc;

      if (!acc[unitType]) {
        acc[unitType] = {
          unit_type: unitType,
          total_views: 0,
          total_conversions: 0,
        };
      }
      acc[unitType].total_views += item.view_count || 0;
      if (item.form_submitted) {
        acc[unitType].total_conversions += 1;
      }
      return acc;
    }, {});

    const conversionByType = Object.values(conversionAggregation || {}).map((item: ConversionAggregation) => ({
      unit_type: item.unit_type,
      total_views: item.total_views,
      total_conversions: item.total_conversions,
      conversion_rate:
        item.total_views > 0
          ? Math.round((item.total_conversions / item.total_views) * 100 * 10) / 10
          : 0,
    }));

    return {
      mostViewedUnits,
      mostClickedUnits,
      hiddenPriceInquiries,
      conversionByType,
    };
  } catch (error) {
    console.error('Error fetching unit interest analytics:', error);
    return null;
  }
}

/**
 * Get deal sync performance (estimated based on form submissions)
 * Note: This is a simplified version. In production, you'd track actual deal sync events
 */
export async function getDealSyncPerformance(): Promise<DealSyncPerformance | null> {
  try {
    // Count total deals (form submissions that should create deals)
    const { count: totalDealsCreated } = await supabase
      .from('unit_interest_tracking')
      .select('*', { count: 'exact', head: true })
      .eq('form_submitted', true);

    // Get recent activity - fetch more for pagination
    const { data: recentData } = await supabase
      .from('unit_interest_tracking')
      .select(
        `
        created_at,
        form_submitted,
        meeting_scheduled,
        contact_email,
        property_units!inner(unit_number)
      `
      )
      .order('created_at', { ascending: false })
      .limit(100); // Increased limit for pagination support

    interface RecentActivityItem {
      created_at: string;
      meeting_scheduled?: boolean;
      form_submitted?: boolean;
      contact_email: string;
      property_units?: {
        unit_number: string;
      };
    }
    const recentActivity = recentData?.map((item: RecentActivityItem) => ({
      timestamp: item.created_at,
      status: item.meeting_scheduled
        ? 'Meeting Scheduled'
        : item.form_submitted
        ? 'Deal Created'
        : 'Lead Captured',
      unit_number: item.property_units?.unit_number,
      contact_email: item.contact_email,
    })) || [];

    return {
      totalDealsCreated: totalDealsCreated || 0,
      successRate: 95, // Simplified - in production, track actual sync success/failure
      failedSyncs: Math.round((totalDealsCreated || 0) * 0.05),
      recentActivity,
    };
  } catch (error) {
    console.error('Error fetching deal sync performance:', error);
    return null;
  }
}

/**
 * Get hero KPI metrics for dashboard
 */
export async function getHeroKPIs() {
  try {
    const [leadMetrics, dealPerformance] = await Promise.all([
      getLeadCaptureMetrics(),
      getDealSyncPerformance(),
    ]);

    return {
      totalLeads: leadMetrics?.totalLeads || 0,
      activeDeals: dealPerformance?.totalDealsCreated || 0,
      syncSuccessRate: dealPerformance?.successRate || 0,
      avgEngagement: leadMetrics?.averageEngagementScore || 0,
    };
  } catch (error) {
    console.error('Error fetching hero KPIs:', error);
    return null;
  }
}

/**
 * Get activity trend for a specific number of days
 */
export async function getActivityTrend(days: number = 7) {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data } = await supabase
      .from('unit_interest_tracking')
      .select('created_at')
      .gte('created_at', startDate.toISOString());

    // Group by day
    const trendByDay = data?.reduce((acc: Record<string, number>, item) => {
      const day = new Date(item.created_at).toLocaleDateString();
      acc[day] = (acc[day] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(trendByDay || {}).map(([date, count]) => ({
      date,
      count,
    }));
  } catch (error) {
    console.error('Error fetching activity trend:', error);
    return [];
  }
}

/**
 * Get HubSpot contacts analytics from HubSpot API
 */
export async function getHubSpotContactsAnalytics(): Promise<HubSpotContactsAnalytics | null> {
  try {
    const { data, error } = await supabase.functions.invoke('get-hubspot-analytics', {
      body: {},
    });

    if (error) {
      console.error('Error calling get-hubspot-analytics Edge Function:', error);
      return null;
    }

    if (data?.success && data?.data?.contacts) {
      return data.data.contacts;
    }

    return null;
  } catch (error) {
    console.error('Error fetching HubSpot contacts analytics:', error);
    return null;
  }
}

/**
 * Get HubSpot deals analytics from HubSpot API
 */
export async function getHubSpotDealsAnalytics(): Promise<HubSpotDealsAnalytics | null> {
  try {
    const { data, error } = await supabase.functions.invoke('get-hubspot-analytics', {
      body: {},
    });

    if (error) {
      console.error('Error calling get-hubspot-analytics Edge Function:', error);
      return null;
    }

    if (data?.success && data?.data?.deals) {
      return data.data.deals;
    }

    return null;
  } catch (error) {
    console.error('Error fetching HubSpot deals analytics:', error);
    return null;
  }
}

/**
 * Get HubSpot engagement metrics from HubSpot API
 */
export async function getHubSpotEngagementMetrics(): Promise<HubSpotEngagementMetrics | null> {
  try {
    const { data, error } = await supabase.functions.invoke('get-hubspot-analytics', {
      body: {},
    });

    if (error) {
      console.error('Error calling get-hubspot-analytics Edge Function:', error);
      return null;
    }

    if (data?.success && data?.data?.engagement) {
      return data.data.engagement;
    }

    return null;
  } catch (error) {
    console.error('Error fetching HubSpot engagement metrics:', error);
    return null;
  }
}

/**
 * Get all HubSpot analytics data in one call
 */
export async function getAllHubSpotAnalytics(): Promise<{
  contacts: HubSpotContactsAnalytics | null;
  deals: HubSpotDealsAnalytics | null;
  engagement: HubSpotEngagementMetrics | null;
} | null> {
  try {
    const { data, error } = await supabase.functions.invoke('get-hubspot-analytics', {
      body: {},
    });

    if (error) {
      console.error('Error calling get-hubspot-analytics Edge Function:', error);
      return null;
    }

    if (data?.success && data?.data) {
      return data.data;
    }

    return null;
  } catch (error) {
    console.error('Error fetching HubSpot analytics:', error);
    return null;
  }
}
